import { Link } from "react-router-dom";
import { Phone, LifeBuoy, HeartPulse, Users, School, Shield, BookOpen, Download, AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { listResources, resourceDownloadUrl } from "../lib/api";

const TOPICS = [
  { icon: HeartPulse, title: "Emotional Wellness", body: "Naming feelings, building self-awareness, and healthy routines that strengthen the mind." },
  { icon: Shield, title: "Stress & Anxiety", body: "Grounding techniques, routines, and when to ask for extra support." },
  { icon: AlertTriangle, title: "Depression Awareness", body: "Signs to watch for, how to listen without judgment, and how to connect to help." },
  { icon: HeartPulse, title: "Coping with Anger", body: "Pause, name it, breathe, respond — skills that work in school, home, and life." },
  { icon: BookOpen, title: "Grief & Loss", body: "Everyone grieves differently. We create safe space to feel and heal together." },
  { icon: Users, title: "Bullying Prevention", body: "What bullying is, what it isn't, and the role every bystander can play." },
  { icon: Users, title: "Healthy Friendships", body: "Picking friends who build you up — and knowing when it's time to walk away." },
  { icon: LifeBuoy, title: "Asking For Help", body: "Reaching out is a strength. Here's how to do it and who to trust." },
];

const AUDIENCES = [
  { icon: Users, title: "For Youth", body: "Easy-to-read guides, videos, and conversation prompts just for you.", to: "/resources?category=Youth+Resources" },
  { icon: HeartPulse, title: "For Parents & Caregivers", body: "How to support a struggling child, start the conversation, and find help.", to: "/parents" },
  { icon: Shield, title: "For Mentors", body: "Facilitator guidance, de-escalation tips, and when to escalate.", to: "/resources?category=Facilitator+Manuals" },
  { icon: School, title: "For Schools", body: "Partnership packets, lesson overviews, and classroom implementation guides.", to: "/resources?category=School+Partnerships" },
];

export default function StrongMindsCenter() {
  const { data: res = [] } = useQuery({
    queryKey: ["public-resources", "Mental Wellness"],
    queryFn: () => listResources("Mental Wellness"),
  });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="smc-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Strong Minds Resource Center</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight">
              Every young man deserves to be <span className="italic text-[#F5B90A]">heard, supported, and empowered.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              Education, tools, and support for families, schools, and mentors. Mental wellness and healthy choices are learned
              skills — and we're here to help you build them.
            </p>
          </div>
        </div>
      </section>

      {/* CRISIS BANNER */}
      <section className="bg-[#F5B90A] text-black py-6" data-testid="smc-crisis">
        <div className="container-x flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <LifeBuoy className="h-6 w-6" />
            <div>
              <p className="font-bold">In crisis or thinking of hurting yourself?</p>
              <p className="text-sm">Call or text <strong>988</strong> (Suicide & Crisis Lifeline) — free, confidential, 24/7.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Phone className="h-5 w-5" />
            <p className="text-sm"><strong>Immediate danger?</strong> Call <strong>911</strong>.</p>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section className="bg-white py-20 md:py-24" data-testid="smc-topics">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline text-[#6B21A8]">What We Cover</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Topics for strong minds.</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {TOPICS.map((t, i) => (
              <div key={t.title} className="card-editorial p-6" data-testid={`smc-topic-${i}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#6B21A8]/10 text-[#6B21A8]">
                  <t.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold">{t.title}</h3>
                <p className="mt-2 text-sm text-black/70">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE PATHWAYS */}
      <section className="bg-[#F7F3EC] py-20 md:py-24" data-testid="smc-audiences">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline text-[#6B21A8]">Find Your Path</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Who is this for?</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {AUDIENCES.map((a) => (
              <Link key={a.title} to={a.to} className="group card-editorial p-7 block bg-white" data-testid={`smc-audience-${a.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#111]/5 text-[#111]">
                  <a.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold">{a.title}</h3>
                <p className="mt-2 text-sm text-black/70">{a.body}</p>
                <p className="mt-5 text-sm font-semibold text-[#6B21A8] group-hover:underline">Open →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* RESOURCES */}
      <section className="bg-white py-20 md:py-24" data-testid="smc-resources">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="overline text-[#6B21A8]">Downloadable Guides</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Mental Wellness Library</h2>
            </div>
            <Link to="/resources" className="text-sm font-semibold text-[#6B21A8] hover:underline">Browse all resources →</Link>
          </div>
          {res.length === 0 ? (
            <p className="mt-10 text-black/60 italic">New mental wellness guides are being prepared. Check back soon.</p>
          ) : (
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {res.map((r) => (
                <a key={r.id} href={resourceDownloadUrl(r.id)} target="_blank" rel="noreferrer" className="card-editorial p-6 block" data-testid={`smc-resource-${r.id}`}>
                  <p className="overline text-[#6B21A8]">{r.category}</p>
                  <h4 className="mt-2 font-display text-lg font-bold">{r.title}</h4>
                  {r.description && <p className="mt-2 text-sm text-black/70">{r.description}</p>}
                  <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-black/80">
                    <Download className="h-4 w-4" /> Download PDF
                  </p>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="bg-[#0A0A0A] text-white py-14" data-testid="smc-disclaimer">
        <div className="container-x">
          <div className="mx-auto max-w-3xl rounded-lg border border-white/15 p-8">
            <p className="overline text-[#F5B90A]">A Note on Our Role</p>
            <p className="mt-3 text-white/85">
              Change Center Mentoring Group provides educational mentoring and prevention services. We do not provide
              clinical diagnosis, psychotherapy, or emergency mental health treatment. When needed, we help connect
              families to qualified community resources.
            </p>
            <p className="mt-4 text-sm text-white/60">
              In crisis? Call or text <strong className="text-[#F5B90A]">988</strong>. Immediate danger? Call <strong className="text-[#F5B90A]">911</strong>.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
